import { FC, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { getInsurances } from 'services/APIs';
import { Insurance } from 'interfaces/insuranceType';
import { Autocomplete } from '@material-ui/lab';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import {
  BookingInformation,
  updateStep3,
  updateBookingInformation,
} from 'redux/bookingFormSlice';
import styles from './InsuranceSection.module.scss';
import { Asterix } from 'components/Form/HelperText/HelperText';
import { ReactComponent as CheckCircleIcon } from 'assets/icons/check_circle.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import { INSURANCE_PAYMENT_METHOD_OPTIONS } from 'pages/ClinicDetailsPage/BookNowPanel/utils/constants';
import { ReactComponent as DropdownIcon } from 'assets/icons/arrow-down.svg';
import { checkExistingPractitionerAcceptInsurance } from 'utils/checkExistingPractitionerAcceptInsurance';

interface InsuranceSectionProps {
  clinicId: string;
  isPreferredDoctor: boolean;
  onFindOtherPractitioners?: () => void;
}

const InsuranceSection: FC<InsuranceSectionProps> = ({
  clinicId,
  isPreferredDoctor,
  onFindOtherPractitioners,
}) => {
  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const [options, setOptions] = useState<Insurance[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
    bookingFormSlice: {
      step3: {
        currentInsurance,
        isPayForMyself,
        insurancePaymentMethod,
        patientInfo,
      },
      selectedLastInsurance,
      bookingInformation,
    },
    clinicDetailsSlice: {
      clinicData: { isInsuranceEnabled, phone, name },
    },
  } = useAppSelector((state) => state);
  const dispatch = useAppDispatch();
  const lastDispatchedDentistRef = useRef<string | null>(null);
  const originalDentistIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (originalDentistIdRef.current !== null) return;
    originalDentistIdRef.current =
      bookingInformation?.type === 'clinic'
        ? (bookingInformation.dentist?.id ?? null)
        : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerSectionClassName = cx({
    [styles['headerSection']]: true,
    [styles['headerSection-iframe']]: isLoadedInsideIframe,
  });

  const bodySectionClassName = cx({
    [styles['bodySection']]: true,
    [styles['bodySection-iframe']]: isLoadedInsideIframe,
  });

  const onInsuranceChange = (value: Insurance | null) => {
    dispatch(
      updateStep3({
        currentInsurance: value,
      })
    );
  };

  const onCheckedChange = () => {
    const currentCheck = isPayForMyself;

    dispatch(
      updateStep3({
        isPayForMyself: !currentCheck,
      })
    );

    if (!isPayForMyself) {
      // If checked, clear insurance info
      dispatch(
        updateStep3({
          currentInsurance: null,
          insurancePaymentMethod: null,
        })
      );
    }
  };

  const onChangeInsurancePaymentMethod = (value: any) => {
    if (isPayForMyself) {
      dispatch(
        updateStep3({
          insurancePaymentMethod: value,
        })
      );
    }
  };

  useEffect(() => {
    const getInsuranceList = async () => {
      setIsLoading(true);

      const data = await getInsurances(clinicId);

      setIsLoading(false);

      if (data.error) return;

      const patientsAlreadyUsedInsurance = patientInfo?.insuranceCoverages?.[0];

      const insurance = data.data?.find((insurance) => {
        if (selectedLastInsurance) {
          return insurance.id === selectedLastInsurance.clinicInsuranceId;
        }

        return insurance.id === patientsAlreadyUsedInsurance?.clinicInsuranceId;
      });

      if (insurance && !isPayForMyself) {
        dispatch(
          updateStep3({
            currentInsurance: insurance,
          })
        );
      }

      setOptions(data.data ?? []);
    };
    getInsuranceList();

    return () => {};
  }, [
    clinicId,
    dispatch,
    patientInfo?.insuranceCoverages,
    selectedLastInsurance,
    isPayForMyself,
  ]);

  useEffect(() => {
    if (!bookingInformation || bookingInformation.type !== 'clinic') return;

    if (isPayForMyself) {
      if (lastDispatchedDentistRef.current !== null) {
        lastDispatchedDentistRef.current = null;
        dispatch(
          updateBookingInformation({
            ...bookingInformation,
            dentist: {
              ...bookingInformation.dentist,
              id: originalDentistIdRef.current ?? null,
            },
          } as BookingInformation)
        );
      }
      return;
    }

    if (!currentInsurance) return;

    const { dentistId } = checkExistingPractitionerAcceptInsurance({
      doctors: bookingInformation.insurancesByAvailableDoctors || [],
      practitionerId: bookingInformation.practitioner.id,
      currentInsuranceCarrierId: currentInsurance.insuranceId,
      isPreferredDoctor,
      isPayForMyself,
      isExamCleaningService: bookingInformation.isExamCleaningService,
      dentistId: bookingInformation.dentist?.id || undefined,
    });

    if (dentistId && dentistId !== lastDispatchedDentistRef.current) {
      lastDispatchedDentistRef.current = dentistId;
      dispatch(
        updateBookingInformation({
          ...bookingInformation,
          dentist: { ...bookingInformation.dentist, id: dentistId },
        } as BookingInformation)
      );
    }
  }, [
    currentInsurance,
    bookingInformation,
    isPreferredDoctor,
    isPayForMyself,
    dispatch,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderInsuranceMessage = (currentInsurance: Insurance) => {
    if (!currentInsurance.isActive) {
      return (
        <div className={styles['inactive-warning']}>
          <span>
            {name} does not allow this insurance provider to schedule online.
          </span>
          <span>
            Please call us at{' '}
            <a href={`tel:+${phone}`}>{formatFullPhoneNumber(phone)} </a>
            to schedule your appointment.
          </span>
        </div>
      );
    }

    const {
      isAcceptedInsurance: isExistingPractitionerAcceptInsurance,
      doctorInsurance,
    } = checkExistingPractitionerAcceptInsurance({
      doctors: bookingInformation?.insurancesByAvailableDoctors || [],
      practitionerId: bookingInformation?.practitioner?.id || '',
      currentInsuranceCarrierId: currentInsurance.insuranceId,
      isPreferredDoctor,
      isPayForMyself,
      isExamCleaningService: bookingInformation?.isExamCleaningService || false,
      dentistId: bookingInformation?.dentist?.id || undefined,
    });

    if (isExistingPractitionerAcceptInsurance) {
      return (
        <div className={styles['info']}>
          {doctorInsurance
            ? `${doctorInsurance.isInNetwork ? 'In-network' : 'Out-of-network'} - Insurance accepted by ${name}`
            : `Insurance accepted by ${name}`}
        </div>
      );
    }

    if (
      isPreferredDoctor ||
      (bookingInformation?.isExamCleaningService &&
        bookingInformation?.dentist?.id)
    ) {
      return (
        <div className={styles['practitioner-warning']}>
          <span>
            This practitioner does not accept {currentInsurance.name}. Please
            choose another insurance provider{' '}
            {onFindOtherPractitioners && (
              <>
                <span>or </span>
                <button
                  className={styles['find-others-link']}
                  onClick={onFindOtherPractitioners}
                >
                  choose another practitioner{' '}
                </button>
                <span>
                  - or call us at{' '}
                  <a href={`tel:+${phone}`}>{formatFullPhoneNumber(phone)}</a>{' '}
                  for help.
                </span>
              </>
            )}
          </span>
        </div>
      );
    } else {
      return (
        <div className={styles['practitioner-warning']}>
          <span>
            We don't currently have a practitioner who accepts this insurance.
            Please choose another insurance provider or call us at{' '}
            <a href={`tel:+${phone}`}>{formatFullPhoneNumber(phone)}.</a>
          </span>
        </div>
      );
    }
  };

  if (!isInsuranceEnabled) {
    return null;
  }

  return (
    <section className={styles['section']}>
      <div className={headerSectionClassName}>
        <div className={styles['title']}>Insurance Information</div>
      </div>
      <div className={bodySectionClassName}>
        <h3>
          Insurance provider <Asterix error={'error'} />
        </h3>
        <Autocomplete
          options={options}
          value={currentInsurance}
          getOptionLabel={(option) => option.name}
          classes={{
            option: styles['popper-insurance-item'],
            noOptions: styles['popper-insurance-item'],
          }}
          onChange={(_, value) => onInsuranceChange(value)}
          loading={isLoading}
          disabled={isPayForMyself}
          renderInput={(params) => (
            <div
              className={styles['input-wrapper']}
              ref={params.InputProps.ref}
            >
              {currentInsurance ? (
                <>
                  <button
                    className={styles['selected-insurance']}
                    onClick={() => onInsuranceChange(null)}
                  >
                    <div>
                      {currentInsurance.isActive ? (
                        <CheckCircleIcon className={styles['check-circle']} />
                      ) : (
                        <WarningIcon className={styles['warning']} />
                      )}
                      <span>{currentInsurance.name}</span>
                    </div>
                  </button>
                  {renderInsuranceMessage(currentInsurance)}
                </>
              ) : (
                <input
                  placeholder="Select Insurance Provider"
                  type="text"
                  {...params.inputProps}
                />
              )}
            </div>
          )}
        />

        <div className={styles['seperator']}></div>

        <div className={styles['checkbox-section']}>
          <FormControlLabel
            checked={isPayForMyself}
            onChange={onCheckedChange}
            classes={{ label: styles['label'] }}
            control={<Checkbox classes={{ input: styles['input'] }} />}
            label="I'm paying for myself"
          />
        </div>

        {isPayForMyself && (
          <div className={styles['insurance-payment-method-section']}>
            <Autocomplete
              options={INSURANCE_PAYMENT_METHOD_OPTIONS}
              value={insurancePaymentMethod}
              getOptionLabel={(option) => option?.label}
              classes={{
                option: styles['popper-insurance-item'],
                noOptions: styles['popper-insurance-item'],
              }}
              popupIcon={<DropdownIcon />}
              closeIcon={<></>}
              onChange={(_, value) => onChangeInsurancePaymentMethod(value)}
              renderInput={(params) => (
                <div {...params.InputProps} className={styles['input-wrapper']}>
                  {insurancePaymentMethod ? (
                    <>
                      <button
                        type="button"
                        className={styles['selected-insurance']}
                        onClick={(e) => {
                          e.stopPropagation();
                          onChangeInsurancePaymentMethod(null);
                        }}
                      >
                        <div>
                          <span>{insurancePaymentMethod.label}</span>
                        </div>
                      </button>
                    </>
                  ) : (
                    <input
                      {...params.inputProps}
                      placeholder="Select payment option"
                      className={styles['input']}
                      readOnly
                    />
                  )}
                  <div
                    className={styles['insurance-payment-method-section-arrow']}
                  >
                    {params.InputProps.endAdornment}
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default InsuranceSection;
